import { SettingsProps } from 'client/components/core/Sidebar/components/Settings/entities'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { useCalculateTypography } from 'src/editor/shared/hooks/use-calculate-typography'
import { useThemeColor } from 'src/editor/shared/hooks/use-theme-color'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { TypographyGroup } from 'src/editor/shared/settings/groups/typography-group'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { FormInterface } from 'src/shared/entities/form/form.schema'
import { CheckboxCommonStylesInterface } from 'src/shared/entities/form/form-checkbox-sub-entity-common-styles.schema'

interface FormCheckboxesCommonSettingsProps
  extends SettingsProps<FormInterface> {}

export const FormCheckboxesCommonSettings = ({
  entity,
  defaultEntity,
}: FormCheckboxesCommonSettingsProps) => {
  const updateEntity = useUpdateEntity<FormInterface>()
  const updateProp = useUpdatePropNew(entity as FormInterface)

  const updateCommonSettings = (
    checkboxesCommonStyles: CheckboxCommonStylesInterface,
  ) => {
    updateEntity({
      ...entity,
      checkboxesCommonStyles,
    })
  }

  const commonStyles = entity.checkboxesCommonStyles
  const firstUpdateKeys = ['checkboxesCommonStyles'] as const

  const {
    text: { paragraphTextColor: globalTextColor },
  } = useThemeColor()
  const {
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateTypography({
    fontSize: commonStyles.fontSize,
    mobileFontSize: commonStyles.mobileFontSize,
    lineHeight: commonStyles.lineHeight,
    mobileLineHeight: commonStyles.mobileLineHeight,
    textAlign: commonStyles.textAlign || 'left',
    mobileTextAlign: commonStyles.mobileTextAlign || 'left',
    fontFamily: commonStyles.fontFamily,
    mobileFontFamily: commonStyles.mobileFontFamily,
    fontWeight: commonStyles.fontWeight,
    mobileFontWeight: commonStyles.mobileFontWeight,
    fontStyle: commonStyles.fontStyle,
    mobileFontStyle: commonStyles.mobileFontStyle,
  })

  return (
    <>
      <TypographyGroup
        withoutHorizontalLine
        entity={commonStyles}
        updateEntity={updateCommonSettings}
        fontFamilyKey="fontFamily"
        fontWeightKey="fontWeight"
        fontStyleKey="fontStyle"
        fontFileIdKey="fontFileId"
        mobileFontFamilyKey="mobileFontFamily"
        mobileFontWeightKey="mobileFontWeight"
        mobileFontStyleKey="mobileFontStyle"
        mobileFontFileIdKey="mobileFontFileId"
        fontFamily={calculatedFontFamily.desktop}
        mobileFontFamily={calculatedFontFamily.mobile}
        fontWeight={calculatedFontWeight.desktop}
        mobileFontWeight={calculatedFontWeight.mobile}
        fontStyle={calculatedFontStyle.desktop}
        mobileFontStyle={calculatedFontStyle.mobile}
        fontSizeKey="fontSize"
        mobileFontSizeKey="mobileFontSize"
        fontSize={calculatedFontSize.desktop}
        mobileFontSize={calculatedFontSize.mobile}
        updateFontSize={updateProp(...firstUpdateKeys, 'fontSize')}
        updateMobileFontSize={updateProp(...firstUpdateKeys, 'mobileFontSize')}
        lineHeightKey="lineHeight"
        mobileLineHeightKey="mobileLineHeight"
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        updateLineHeight={updateProp(...firstUpdateKeys, 'lineHeight')}
        updateMobileLineHeight={updateProp(
          ...firstUpdateKeys,
          'mobileLineHeight',
        )}
        textAlignKey="textAlign"
        mobileTextAlignKey="mobileTextAlign"
        textAlign={calculatedTextAlign.desktop}
        mobileTextAlign={calculatedTextAlign.mobile}
        updateTextAlign={updateProp(...firstUpdateKeys, 'textAlign')}
        updateMobileTextAlign={updateProp(
          ...firstUpdateKeys,
          'mobileTextAlign',
        )}
        hideLetterSpacing
      />

      <GroupTitle
        afterChildren={
          <ResetEntityStyles
            entity={entity}
            defaultEntity={defaultEntity}
            keys={[[...firstUpdateKeys, 'color']]}
            mobileKeys={[[...firstUpdateKeys, 'mobileColor']]}
          />
        }
      >
        website_styles_settings.colors.label
      </GroupTitle>
      <ColorPickerControl
        isClearable
        label="text color"
        color={commonStyles.color || globalTextColor}
        update={updateProp(...firstUpdateKeys, 'color')}
        mobileColor={commonStyles.mobileColor}
        mobileUpdate={updateProp(...firstUpdateKeys, 'mobileColor')}
      />
    </>
  )
}
